import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/StoreHooks';
import { TileContainer } from './ServiceModules.styles';
import { useDrag, useDrop } from 'react-dnd';
import { updateServiceModuleOrderThunk } from '../../store/thunk/serviceModulesThunk';
import { FileServiceModule, ServiceModule, TextServiceModule, UrlServiceModule } from '../../types/serviceModuleTypes';
import { useRootServiceModules } from '../../store/selectors/serviceModulesSelector';
import ServiceModuleCard from './ServiceModuleCard';
import TextDialog from './Folder/Cards/TextDialog';
import FileDialog from './Folder/Cards/FileDialog';

interface TileProps {
    id: number;
    module: ServiceModule;
    moveTile: (id: number, to: number) => void;
    findTile: (id: number) => { module: ServiceModule, index: number };
    onClickDialog?: (module: ServiceModule) => void;
}

interface Item {
    id: number
    originalIndex: number
}

function Tile(props: TileProps & { isReordering: boolean; setIsReordering: (value: boolean) => void }) {
    const { index: originalIndex } = props.findTile(props.id);
    const navigate = useNavigate();
    const location = useLocation();
    const baseLocation = location.pathname.split("/").slice(0, 3).join("/");
    const moduleTypeLowerCase = props.module.type.toLowerCase();

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: 'ServiceModuleTile',
            item: { id: props.id, originalIndex },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            end: (item: Item, monitor) => {
                const { id: droppedId, originalIndex } = item;
                const didDrop = monitor.didDrop();

                if (!didDrop) {
                    props.moveTile(droppedId, originalIndex)
                }
            }
        }),
        [props.id, originalIndex, props.moveTile]
    );

    const [, drop] = useDrop({
        accept: 'ServiceModuleTile',
        hover: (item: Item) => {
            if (item.id !== props.id) {
                const { index } = props.findTile(props.id)
                props.moveTile(item.id, index)
            }
        },
    });

    const handleClick = () => {
        if (isDragging || props.isReordering) return;

        const module = props.module;

        const handleUrl = () => {
            let url = (module as UrlServiceModule).url.trim();
            if (!url.includes('://')) {
                url = `https://${url}`;
            }
            window.open(url, '_blank');
        };

        switch (module.type) {
            case 'Url':
                handleUrl(); break;
            case 'Text':
            case 'File':
                props.onClickDialog?.(module); break;
            default:
                navigate(`${baseLocation}/${moduleTypeLowerCase}/${props.id}`);
        }
    };

    return (
        <div
            ref={(node) => drag(drop(node))}
            style={{ opacity: isDragging ? 0.25 : 1 }}
        >
            <ServiceModuleCard
                module={props.module}
                setIsReordering={props.setIsReordering}
                onCardClick={handleClick}
            />
        </div >
    );
};

interface DragNDropTilesProps {
    serviceModules: ServiceModule[];
    onOrderChanged: (newOrder: ServiceModule[]) => void;
}

function DragNDropTiles(props: DragNDropTilesProps) {
    const [draggedModules, setDraggedModules] = useState<ServiceModule[]>([]);
    const [isReordering, setIsReordering] = useState(false);

    const [selectedTextModule, setSelectedTextModule] = useState<TextServiceModule | null>(null);
    const [selectedFileModule, setSelectedFileModule] = useState<FileServiceModule | null>(null);

    useEffect(() => {
        setDraggedModules([...props.serviceModules].sort((a, b) => a.order - b.order));
    }, [props.serviceModules]);

    const [, drop] = useDrop(() => ({
        accept: 'ServiceModuleTile',
        drop: () => props.onOrderChanged(draggedModules)
    }), [draggedModules])

    const findTile = useCallback((id: number) => {
        const module = draggedModules.filter((m) => m.id === id)[0];
        return {
            module,
            index: draggedModules.indexOf(module)
        };
    }, [draggedModules]);

    const moveTile = useCallback((id: number, to: number) => {
        const { module, index } = findTile(id);
        const updatedTiles = [...draggedModules];
        updatedTiles.splice(index, 1);
        updatedTiles.splice(to, 0, module);
        const reorderedTiles = updatedTiles.map((m, i) => ({ ...m, order: i }));
        setDraggedModules(reorderedTiles);
    }, [draggedModules]);

    const handleOpenDialog = useCallback((module: ServiceModule) => {
        if (module.type === 'Text') setSelectedTextModule(module as TextServiceModule);
        else if (module.type === 'File') setSelectedFileModule(module as FileServiceModule);
    }, []);

    return (
        <>
            <TileContainer ref={drop}>
                {draggedModules.map((module) => (
                    <Tile
                        key={module.id}
                        id={module.id}
                        module={module}
                        moveTile={moveTile}
                        findTile={findTile}
                        isReordering={isReordering}
                        setIsReordering={setIsReordering}
                        onClickDialog={handleOpenDialog}
                    />
                ))}
            </TileContainer>

            {selectedTextModule && selectedTextModule.type === 'Text' && (
                <TextDialog
                    open={!!selectedTextModule}
                    onClose={() => setSelectedTextModule(null)}
                    name={selectedTextModule.name}
                    text={(selectedTextModule as TextServiceModule).text ?? ""}
                />
            )}

            {selectedFileModule && selectedFileModule.type === 'File' && (
                <FileDialog
                    open={!!selectedFileModule}
                    onClose={() => setSelectedFileModule(null)}
                    name={selectedFileModule.name}
                    filename={selectedFileModule.filename}
                    fileUrl={selectedFileModule.downloadUrl ?? selectedFileModule.path}
                    serviceModuleId={selectedFileModule.id}
                    needsReadReceipt={selectedFileModule.needsReadReceipt}
                />
            )}
        </>
    );
};

export default function ServiceModuleTiles() {
    const serviceModules = useRootServiceModules();
    const dispatch = useAppDispatch();

    return <DragNDropTiles
        serviceModules={serviceModules}
        onOrderChanged={(newOrder) => dispatch(updateServiceModuleOrderThunk(newOrder.map(m => m.id)))} />
}
